import { Delete, Edit } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import RemoveIcon from '@mui/icons-material/Remove';
import { Backdrop, Box, Button, LinearProgress, Modal, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { MyToDisableForm } from "../../components";
import { MyConfirmDialog } from "../../components/MyConfirmDialog";
import { MyRenderCell } from "../../components/MyRenderCell";
import { createCourse, ICourse } from "../../models/courses";
import { createEntityToDisable, IEntityToDisable } from "../../models/entityToDisable";
import { addCourse, disableCourse, getCourses, removeCourse } from "../../redux";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from '../../redux/store';
import { MyBoxModal } from "../../styled-components";
import { MyForm } from './components';

interface Props {
    idParent?: string;
    isToAddCourses?: boolean;
}

export const Courses = ({ isToAddCourses, idParent }: Props) => {
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const openDialog = () => setDialogOpen(!dialogOpen);

    const [disableOpen, setDisableOpen] = useState(false);
    const openDisableDialog = () => setDisableOpen(!disableOpen);

    const [editing, setEditing] = useState(false);

    const [courseToEdit, setCourseToEdit] = useState(createCourse({}));
    const [entityToDisable, setEntityToDisable] = useState(createEntityToDisable({}));

    const [pageSize, setPageSize] = useState<number>(10);

    const dispatch = useAppDispatch();
    const { isLoading, courses = [] } = useAppSelector((state: RootState) => state.courses);

    const [confirmAddDialog, setConfirmAdd] = useState(false);
    const openConfirmAddDialog = () => setConfirmAdd(!confirmAddDialog);

    const [confirmRemoveDialog, setConfirmRemove] = useState(false);
    const openConfirmRemoveDialog = () => setConfirmRemove(!confirmRemoveDialog);

    useEffect(() => {
        dispatch(getCourses(idParent) as any);
    }, [dispatch, idParent])

    const onAddCourse = (id: number) => {
        if (idParent)
            dispatch(addCourse(id, idParent) as any);
    }

    const onRemoveCourse = (id: number) => {
        if (idParent)
            dispatch(removeCourse(id, idParent) as any);
    }

    const onDisableDialog = (course: ICourse) => {
        setEntityToDisable(createEntityToDisable(course));
        setEditing(true);
        openDisableDialog();
    }

    const onEdit = (course: ICourse) => {
        setCourseToEdit(createCourse(course));
        setEditing(true);
        openDialog();
    }
    const onAdd = () => {
        setCourseToEdit(createCourse({}));
        setEditing(false);
        openDialog();
    }

    const onDisable = (values: IEntityToDisable) => {
        dispatch(disableCourse(values) as any);
    }

    const onConfirmAdd = (course: ICourse) => {
        setCourseToEdit(createCourse(course));
        openConfirmAddDialog();
    }

    const onConfirmRemove = (course: ICourse) => {
        setCourseToEdit(createCourse(course));
        openConfirmRemoveDialog();
    }

    const Columns: GridColDef[] = [
        {
            field: "action",
            headerName: "Action",
            width: isToAddCourses ? 70 : 210,
            sortable: false,
            align: "right",
            renderCell: (params) => {
                const onClick = (e: React.MouseEvent<HTMLElement>) => {
                    onEdit(params.row as any);
                };
                const disableCourse = (e: React.MouseEvent<HTMLElement>) => {
                    onDisableDialog(params.row as any);
                };
                const addItems = (e: React.MouseEvent<HTMLElement>) => {
                    navigate("/courses/" + params.row.id);
                };
                const addCourse = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmAdd(params.row as any);
                };
                const removeCourse = (e: React.MouseEvent<HTMLElement>) => {
                    onConfirmRemove(params.row as any);
                };
                return (
                    <>
                        {!isToAddCourses && (<>
                            <Tooltip title="Edit" >
                                <Button onClick={onClick}><Edit /></Button>
                            </Tooltip>
                            <Tooltip title="Disable" >
                                <Button onClick={disableCourse}><Delete /></Button>
                            </Tooltip>
                            <Tooltip title="Add Materials" >
                                <Button onClick={addItems}><FormatListNumberedIcon /></Button>
                            </Tooltip>
                        </>)}
                        {isToAddCourses && (params.row.subActivityId ?
                            (<>
                                <Tooltip title="Remove Course" >
                                    <Button onClick={removeCourse}><RemoveIcon /></Button>
                                </Tooltip>
                            </>) :
                            (<>
                                <Tooltip title="Add Course" >
                                    <Button onClick={addCourse}><AddIcon /></Button>
                                </Tooltip>
                            </>))}
                    </>
                )
            }
        },
        {
            field: 'id',
            headerName: 'id',
            width: 100,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'code',
            headerName: 'Code',
            width: 200,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 500,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
        {
            field: 'activeUntilDate',
            headerName: 'Active Until Date',
            width: 250,
            renderCell: (params) => (<MyRenderCell params={params} />)
        },
    ]

    return (
        <div>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <Box sx={{ width: '40%' }}>
                    <LinearProgress />
                </Box>
            </Backdrop>
            {!isToAddCourses && (
                <>
                    <Typography variant="h4" component="h1">Courses</Typography>
                    <br />
                    <Button onClick={onAdd}>Add</Button>
                </>
            )}
            <div style={{ height: "calc(100vh - 250px)", width: '100%', flexGrow: 1 }}>
                <DataGrid
                    rows={courses}
                    columns={Columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 50, 100]}
                    columnVisibilityModel={{ id: false, }}
                //checkboxSelection
                />
            </div>
            <Modal open={dialogOpen} onClose={openDialog}>
                <MyBoxModal>
                    <MyForm onCancel={openDialog} course={courseToEdit} editing={editing} />
                </MyBoxModal>
            </Modal>
            <Modal open={disableOpen} onClose={openDisableDialog}>
                <MyBoxModal>
                    <MyToDisableForm onCancel={openDisableDialog} entity={entityToDisable} onDisable={onDisable} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmAddDialog} onClose={openConfirmAddDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmAddDialog} entity={courseToEdit} onOk={onAddCourse} />
                </MyBoxModal>
            </Modal>
            <Modal open={confirmRemoveDialog} onClose={openConfirmRemoveDialog}>
                <MyBoxModal>
                    <MyConfirmDialog onCancel={openConfirmRemoveDialog} entity={courseToEdit} onOk={onRemoveCourse} />
                </MyBoxModal>
            </Modal>
        </div >
    )
}
